import React from 'react';

import { ArrowLeft } from 'lucide-react';

import Button from './Button';
import { scrollMainToElement } from '../../utils/scrollUtils';

interface Section {
  id: string;
  title: string;
}

interface FormProps {
  sections: Section[];
  onSave: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  isSubmitting?: boolean;
  hideSectionNav?: boolean;
  formTitle?: string;
  hideActions?: boolean;
  // Skips the section-nav/title area entirely, leaving just the action buttons —
  // for forms rendered somewhere that already has its own title (e.g. inside a Drawer).
  hideHeader?: boolean;
}

const Form: React.FC<FormProps> = ({
  sections,
  onSave,
  onCancel,
  children,
  isSubmitting = false,
  hideSectionNav = false,
  formTitle,
  hideActions = false,
  hideHeader = false,
}) => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);

    if (element) {
      scrollMainToElement(element, 200); // 200 accounts for both nav bars
    }
  };

  return (
    <div className="flex flex-col">
      {/* Top Navigation Bar - Made sticky and flush */}
      <div
        className={
          hideHeader
            ? 'flex justify-end'
            : 'sticky top-32 z-[1] border-b border-primary/30 bg-gray-50 lg:top-0 dark:border-primary/30 dark:bg-secondary'
        }
      >
        <div
          className={
            hideHeader
              ? 'flex items-center'
              : 'flex justify-end md:justify-between items-center px-4 py-2'
          }
        >
          {/* Section Navigation or Title */}
          {hideHeader ? null : !hideSectionNav ? (
            <div className="hidden md:flex space-x-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="cursor-pointer rounded-md px-4 py-2 text-sm font-semibold text-ink-muted transition-colors
                            hover:bg-primary/10 hover:text-primary-deep focus:outline-none
                            dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-primary"
                >
                  {section.title}
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4">
              <h2 className="font-display text-lg font-semibold text-ink dark:text-white">
                {formTitle || 'Form'}
              </h2>
            </div>
          )}

          {/* Action Buttons */}
          {!hideActions && (
            <div className="flex space-x-2">
              {onCancel && (
                <Button variant="secondary" onClick={onCancel} icon={ArrowLeft}>
                  Back
                </Button>
              )}
              <Button
                onClick={onSave}
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <form className="w-full" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-6">{children}</div>
        </form>
      </div>
    </div>
  );
};

export default Form;
